import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { PageTitle } from '@client/components/_ui/display/PageTitle';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { TitleIconChip } from '@client/components/_ui/display/TitleIconChip';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { confirmAction } from '@client/components/_ui/feedback/modal';
import { KebabMenu } from '@client/components/_ui/navigation/KebabMenu';
import { ProductEdit } from '@client/components/products-v2/ProductEdit';
import {
    PublishCheckCount,
    PublishReadinessChip,
    usePublishReadiness,
    useRefreshPublishReadiness,
} from '@client/components/products-v2/PublishReadiness';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { useTitleCacheStorage } from '@client/providers/TitleCacheProvider';
import { trpc } from '@client/trpc/client';
import { isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import type { ShareableRouteContext } from '@client/types/router';
import { downloadBlob } from '@client/utils/common/blob';
import { cn } from '@client/utils/styling/cn';
import { MAX_PAGE_SIZE, type ProductVersionPublishSection } from '@shared/constants';
import { productVersionBundleFileName } from '@shared/schemas/product-version-bundle-file';
import { isTruthy } from '@shared/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Dropdown } from 'antd';
import { Calendar, ChevronDown, Download, Link2, Package, Pencil, Plus, Undo2 } from 'lucide-react';
import { useState } from 'react';

const VERSION_PATH =
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId';

/** The tab of the version page that fixes each part of a version a publish checks. */
const SECTION_TABS = {
    details: `${VERSION_PATH}/details`,
    documents: `${VERSION_PATH}/documents`,
    fields: `${VERSION_PATH}/fields`,
} as const satisfies Record<ProductVersionPublishSection, string>;

function displayVersionName(name: string | null | undefined) {
    const trimmed = name?.trim();

    if (trimmed) {
        return {
            text: trimmed,
            isPlaceholder: false,
        };
    }

    return {
        text: 'Untitled version',
        isPlaceholder: true,
    };
}

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId',
)({
    staticData: {
        metadata: {
            title: 'Product version',
        },
        presentation: 'fullPage',
        ui: {
            widthVariant: 'default',
            className: 'p-0',
        },
    },
    params: {
        parse({ productVersionId }) {
            return {
                productVersionId: BigInt(productVersionId),
            };
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        void trpcUtils.productVersion.getProductVersion
            .ensureData({ id: params.productVersionId })
            .catch(() => undefined);

        return {
            breadcrumb: {
                async title() {
                    const version = await trpcUtils.productVersion.getProductVersion.ensureData({
                        id: params.productVersionId,
                    });

                    return `Version ${version.version} · ${displayVersionName(version.name).text}`;
                },
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductVersionDetails />
        </PageController>
    );
}

function ProductVersionDetails() {
    const titleCache = useTitleCacheStorage();
    const { orgId, productId, productVersionId } = Route.useParams();
    const { trpcClient, trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();
    // The product's edit route sits over the products list; from here it opens in place.
    const [isEditingProduct, setIsEditingProduct] = useState(false);
    // Lodgement v2 (CUR-1407): links belong to the product, so this is a hop, not a version tab.
    // Moving a version between environments as a file (CUR-1893). The file holds the claim
    // form's full schema, so on top of this page's own permission it needs what reading a form needs.
    const { can } = usePermissions();
    const canExportVersion = can('form_schema:read');
    const [isExporting, setIsExporting] = useState(false);

    const getProductQuery = trpc.product.getProduct.useQuery({ id: productId });
    const getProductVersionQuery = trpc.productVersion.getProductVersion.useQuery({ id: productVersionId });
    const listProductVersionsQuery = trpc.productVersion.listProductVersions.useQuery({
        where: {
            productId,
        },
        orderBy: {
            version: 'desc',
        },
        limit: MAX_PAGE_SIZE,
    });

    const cloneMutation = trpc.productVersion.cloneLatestProductVersion.useMutation();
    const publishMutation = trpc.productVersion.publishProductVersion.useMutation();
    const revertMutation = trpc.productVersion.revertProductVersion.useMutation();
    const updateMutation = trpc.productVersion.updateProductVersion.useMutation();

    const { formData: productVersion } = productVersionFormStore.useInit({
        entityLabel: 'Product version',
        data: getProductVersionQuery.data,
        isLoading: getProductVersionQuery.isLoading,
        isReadOnly: getProductVersionQuery.data?.status === 'PUBLISHED',

        async onReload() {
            await getProductVersionQuery.refetch();
        },

        async onSave(_, changedValues) {
            try {
                await updateMutation.mutateAsync({
                    id: productVersionId,
                    set: changedValues,
                });

                if ('name' in changedValues) {
                    titleCache.invalidate('breadcrumb:');
                }

                await trpcUtils.productVersion.invalidate();

                return { success: true };
            } catch (error) {
                return { success: false, error };
            }
        },
    });

    /**
     * What still stops a publish, as the server's own checks report it. The chip, the tab
     * counts and the Publish button all read this, so the page cannot offer a publish the
     * server would refuse.
     */
    const {
        readiness,
        failing,
        countBySection,
        isPending: isReadinessPending,
    } = usePublishReadiness(productVersionId, getProductVersionQuery.data?.status === 'DRAFT');
    useRefreshPublishReadiness(productVersionId);

    if (getProductQuery.isPending || getProductVersionQuery.isPending || listProductVersionsQuery.isPending) {
        return <Loading />;
    }

    if (getProductQuery.error ?? getProductVersionQuery.error ?? listProductVersionsQuery.error) {
        return (
            <InlineQueryError
                error={getProductQuery.error ?? getProductVersionQuery.error ?? listProductVersionsQuery.error}
                onRetry={() => retryFailedQueries([getProductQuery, getProductVersionQuery, listProductVersionsQuery])}
                retrying={isRetryingQueries([getProductQuery, getProductVersionQuery, listProductVersionsQuery])}
            />
        );
    }

    const product = getProductQuery.data;
    const versions = listProductVersionsQuery.data?.rows ?? [];
    const hasDraft = versions.some((item) => item.status === 'DRAFT');
    const isCurrentLoaded = !!productVersion && productVersion.id === productVersionId;
    const isDraft = isCurrentLoaded && productVersion.status === 'DRAFT';
    const isPublished = isCurrentLoaded && productVersion.status === 'PUBLISHED';

    const currentDateInfo = !isCurrentLoaded
        ? ''
        : productVersion.startDate && productVersion.endDate
          ? `${formatDate(productVersion.startDate)} - ${formatDate(productVersion.endDate)}`
          : !productVersion.startDate
            ? isDraft
                ? 'No launch date set'
                : 'No dates set'
            : isDraft
              ? `Launches ${formatDate(productVersion.startDate)}`
              : `${formatDate(productVersion.startDate)} - ongoing`;

    const openTab = (to: (typeof SECTION_TABS)[ProductVersionPublishSection]) =>
        navigate({ to, params: { orgId, productId, productVersionId } });

    const publishCurrentVersion = () => {
        confirmAction({
            title: 'Publish version',
            content:
                'This version will go live. Any changes made after publishing will be tracked in the audit log. Are you sure?',
            okText: 'Publish',
            onOk() {
                publishMutation.mutate(
                    { id: productVersionId },
                    {
                        onSuccess() {
                            void trpcUtils.productVersion.invalidate();
                        },
                        // The readiness can be a moment behind; the publish names what it found.
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const revertCurrentVersion = () => {
        confirmAction({
            title: 'Revert this version to draft?',
            content: 'The version becomes editable again.',
            okText: 'Revert',
            onOk() {
                revertMutation.mutate(
                    { id: productVersionId },
                    {
                        onSuccess() {
                            void trpcUtils.productVersion.invalidate();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const createNewVersion = () => {
        cloneMutation.mutate(
            { productId },
            {
                onSuccess({ id }) {
                    void trpcUtils.productVersion.invalidate();

                    navigate({
                        to: SECTION_TABS.details,
                        params: {
                            orgId,
                            productId,
                            productVersionId: id,
                        },
                    });
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    };

    /** The saved version as a file that Import version reads in another organisation or environment. */
    const exportCurrentVersion = async () => {
        setIsExporting(true);
        try {
            // Asked directly, not through the query cache: the file must hold the version as it
            // is saved now, and a multi-megabyte bundle has no business staying in the cache.
            const bundle = await trpcClient.productVersion.exportProductVersionBundle.query({ id: productVersionId });
            downloadBlob(
                new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }),
                productVersionBundleFileName(bundle.product.name, bundle.source.versionNumber),
            );
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const versionLabel = (item: { version: number; name: string | null }) => {
        const { text, isPlaceholder } = displayVersionName(item.name);

        return (
            <>
                Version {item.version} · <span className={cn(isPlaceholder && 'italic')}>{text}</span>
            </>
        );
    };

    return (
        <div className="px-7 pt-5 pb-4">
            <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <TitleIconChip icon={<Package />} />
                <PageTitle className="wrap-anywhere min-w-0">{product?.name}</PageTitle>
                <span className="text-muted-foreground text-sm">
                    {[product?.Insurer?.name, product?.type].filter(isTruthy).join(' · ')}
                </span>

                <div className="flex-1" />

                <Button.Link
                    variant="tertiary"
                    size="small"
                    icon={<Link2 />}
                    to="/console/$orgId/configuration/claims-products/products-v2/details/$productId/lodgement-links"
                    params={{ orgId, productId }}
                >
                    Lodgement links
                </Button.Link>
                <KebabMenu
                    label="More"
                    items={[
                        {
                            key: 'edit-product',
                            icon: <Pencil size={14} />,
                            label: 'Edit product',
                            onClick() {
                                setIsEditingProduct(true);
                            },
                        },
                        canExportVersion &&
                            isCurrentLoaded && {
                                key: 'export',
                                icon: <Download size={14} />,
                                // Held while a file is being prepared, so reopening the menu cannot start a second one.
                                disabled: isExporting,
                                label: isExporting ? 'Exporting…' : 'Export',
                                onClick() {
                                    void exportCurrentVersion();
                                },
                            },
                        isPublished && {
                            key: 'revert',
                            icon: <Undo2 size={14} />,
                            // One draft at a time: reverting waits until the existing draft is published or removed.
                            disabled: hasDraft || revertMutation.isPending,
                            label: (
                                <>
                                    Revert to draft
                                    {hasDraft && (
                                        <span className="block text-subtle-foreground text-xs">
                                            This product already has a draft
                                        </span>
                                    )}
                                </>
                            ),
                            onClick: revertCurrentVersion,
                        },
                    ].filter(isTruthy)}
                />
                {!hasDraft && (
                    <Button size="small" icon={<Plus />} loading={cloneMutation.isPending} onClick={createNewVersion}>
                        New version
                    </Button>
                )}
                {isDraft && (
                    <>
                        <PublishReadinessChip
                            readiness={readiness}
                            onFix={(section) => openTab(SECTION_TABS[section])}
                        />
                        <Tooltip
                            title={
                                failing.length
                                    ? `Fix ${failing.length} item${failing.length === 1 ? '' : 's'} before publishing`
                                    : undefined
                            }
                        >
                            {/* A disabled button fires no hover events, so the tooltip listens on the wrapper. */}
                            <span>
                                <Button
                                    type="primary"
                                    size="small"
                                    // Held until the checks answer. If that read fails, Publish stays
                                    // available and the publish itself names what it found.
                                    disabled={isReadinessPending || failing.length > 0}
                                    loading={publishMutation.isPending}
                                    onClick={publishCurrentVersion}
                                >
                                    Publish version
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            </div>

            <div className="mb-3.5 flex flex-wrap items-center gap-3">
                <Dropdown
                    trigger={['click']}
                    onOpenChange={(open) => {
                        // Prefetch the other versions while the menu is open so switching
                        // does not land on a full-page loader (the rail used hover prefetch).
                        if (open) {
                            for (const item of versions) {
                                if (item.id !== productVersionId) {
                                    void trpcUtils.productVersion.getProductVersion
                                        .ensureData({ id: item.id })
                                        .catch(() => undefined);
                                }
                            }
                        }
                    }}
                    menu={{
                        items: versions.map((item) => {
                            const dateRange =
                                item.startDate && item.endDate
                                    ? `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`
                                    : item.startDate
                                      ? `${formatDate(item.startDate)} - ongoing`
                                      : 'No dates set';

                            return {
                                key: String(item.id),
                                label: (
                                    <div className="flex min-w-64 items-center justify-between gap-3 py-0.5">
                                        <div className="min-w-0">
                                            <p
                                                className={cn(
                                                    'truncate font-medium text-sm',
                                                    item.id === productVersionId && 'text-accent-700',
                                                )}
                                            >
                                                {versionLabel(item)}
                                            </p>
                                            <p className="text-muted-foreground text-xs">{dateRange}</p>
                                        </div>
                                        <StatusBadge variant="outlined" module="productVersion" status={item.status} />
                                    </div>
                                ),
                                onClick() {
                                    if (item.id !== productVersionId) {
                                        navigate({
                                            to: SECTION_TABS.details,
                                            params: {
                                                orgId,
                                                productId,
                                                productVersionId: item.id,
                                            },
                                        });
                                    }
                                },
                            };
                        }),
                    }}
                >
                    <button
                        type="button"
                        className="flex cursor-pointer items-center gap-2 rounded-lg border border-separator bg-card px-3 py-1.5 transition-colors hover:bg-muted"
                    >
                        <span className="max-w-72 truncate font-semibold text-sm">
                            {isCurrentLoaded ? versionLabel(productVersion) : 'Version'}
                        </span>
                        <ChevronDown size={14} className="text-subtle-foreground" />
                    </button>
                </Dropdown>

                {isCurrentLoaded && <StatusBadge module="productVersion" status={productVersion.status} />}

                <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                    <Calendar size={14} />
                    {currentDateInfo}
                </span>
            </div>

            {isCurrentLoaded && (
                <Tabs
                    items={[
                        {
                            label: <TabLabel count={countBySection('details')}>Details</TabLabel>,
                            to: SECTION_TABS.details,
                        },
                        {
                            label: <TabLabel count={countBySection('documents')}>Documents</TabLabel>,
                            to: SECTION_TABS.documents,
                        },
                        {
                            // Before Fields: a contact type's required fields are mapped there.
                            label: 'Contact types',
                            to: `${VERSION_PATH}/contact-types`,
                        },
                        {
                            label: <TabLabel count={countBySection('fields')}>Fields</TabLabel>,
                            to: SECTION_TABS.fields,
                        },
                    ]}
                />
            )}

            {isEditingProduct && (
                <ProductEdit action={['edit', productId]} afterClose={() => setIsEditingProduct(false)} />
            )}
        </div>
    );
}

function TabLabel({ children, count }: { children: string; count: number }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            {children}
            <PublishCheckCount count={count} />
        </span>
    );
}
